import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ProductExportConfigurationContext } from '../../../../../../../context/ProductExportConfigurationContext';
import ProductExportConfigurationForm, {
  DraggableProductExportFieldIdentifier,
} from '../Common/ProductExportConfigurationForm';
import DeleteProductExportConfigurationModal from './Modals/DeleteProductExportConfigurationModal';
import { Routing } from '../../../../../../../util/routes.util';
import {
  ProductExportConfiguration,
  ProductExportConfigurationField,
} from '../../../../../../../types/product_export_configuration/productExportConfiguration';
import UpdateButton from '../../../../../../components/Button/templates/UpdateButton';
import { BackButton, ButtonVariant, DeleteButton } from '../../../../../../components/Button';
import { DraggableItemData } from '../../../../../Common/DraggableItem';
import { useRequest } from '../../../../../../../requests/common/requestHook';
import { ProductExportConfigurationRequest } from '../../../../../../../requests/productExportConfiguration.request';
import { ProductExportConfigurationInput } from '../../../../../../../types/product_export_configuration';
import { CompanyContext } from '../../../../../../../context/CompanyContext';

interface UpdateProductExportConfigurationPaneProps {}

export default function UpdateProductExportConfigurationPane({}: UpdateProductExportConfigurationPaneProps) {
  const id = useParams()['id'] || '';
  if (!id) return null; // TODO: Entity not found

  const navigate = useNavigate();
  const { currentCompany } = useContext(CompanyContext);

  const { productExportConfigurations, setProductExportConfigurations, productExportConfigurationsLoading } =
    useContext(ProductExportConfigurationContext);

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);

  const existing = productExportConfigurations.get(id);

  const [input, setInput] = useState<ProductExportConfigurationInput>(
    new ProductExportConfigurationInput(existing || ProductExportConfiguration.default(currentCompany.id)),
  );

  useEffect(() => {
    if (!productExportConfigurationsLoading && existing) {
      setInput(new ProductExportConfigurationInput(existing));
    }
  }, [productExportConfigurationsLoading]);

  const [update, { loading: updateLoading }] = useRequest(ProductExportConfigurationRequest.update);

  const handleUpdate = async (
    configuration: ProductExportConfigurationInput,
    items: DraggableProductExportFieldIdentifier[],
  ) => {
    configuration.mapping = DraggableItemData.toMap<ProductExportConfigurationField>(items);

    const result = await update({ data: [configuration.forUpdate()] });
    if (!result) return;

    const config = result[0];
    productExportConfigurations.set(config.id, new ProductExportConfiguration(config));
    setProductExportConfigurations(new Map(productExportConfigurations));
    navigate(Routing.stock.products.settings.report.overview());
  };

  const footer = (configuration: ProductExportConfigurationInput, items: DraggableProductExportFieldIdentifier[]) => {
    return (
      <div className='flex justify-between'>
        <div>
          <DeleteButton
            disabled={productExportConfigurationsLoading || updateLoading}
            onClick={() => setOpenDeleteModal(true)}
          />
        </div>
        <div className='flex items-center gap-2'>
          <BackButton
            disabled={updateLoading}
            variant={ButtonVariant.secondary}
            onClick={() => navigate(Routing.stock.products.settings.report.overview())}
          />
          <UpdateButton
            loading={updateLoading}
            disabled={productExportConfigurationsLoading || !configuration.filename || !configuration.name}
            onClick={() => handleUpdate(configuration, items)}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      <DeleteProductExportConfigurationModal open={openDeleteModal} setOpen={setOpenDeleteModal} ids={new Set([id])} />
      <ProductExportConfigurationForm
        loading={productExportConfigurationsLoading}
        input={input}
        setInput={setInput}
        footer={footer}
      />
    </>
  );
}
